import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import EnquireModal from '../components/EnquireModal';
import { productsAPI, getImageUrl } from '../utils/api';

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [enquireModalOpen, setEnquireModalOpen] = useState(false);

  useEffect(() => {
    console.log('🔍 ProductDetails: ID changed to:', id);
    const fetchProduct = async () => {
      console.log('🔍 ProductDetails: Fetching product...');
      try {
        const response = await productsAPI.getById(id);
        console.log('🔍 ProductDetails: Fetch SUCCESS:', response.data?.name);
        setProduct(response.data);
      } catch (error) {
        console.error('🔍 ProductDetails: Fetch ERROR:', error);
        if (error.response && error.response.status === 404) {
          console.error('Product not found in DB - check if ID is correct and product exists.');
        }
      } finally {
        console.log('🔍 ProductDetails: Loading set to false');
        setLoading(false);
      }
    };
    if (id) {
      setLoading(true); // Reset loading when ID changes
      setProduct(null); // Reset product when ID changes
      fetchProduct();
    }
  }, [id]);

  if (loading) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-600">Product not found</p>
          <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">Go back home</Link>
        </div>
      </>
    );
  }

  return (
    <>

      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-8 page-bg-graphics">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <nav className="text-sm text-gray-600" data-aos="fade-right">
            <Link to="/" className="hover:text-blue-600 transition">Home</Link> /
            <Link to="/categories" className="hover:text-blue-600 transition">Products</Link> /
            <span className="text-industrial-navy font-semibold">{product.name}</span>
          </nav>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="grid lg:grid-cols-12 gap-12 items-start mb-16">

            {/* Left Column: Image, Badges, and Enquiry */}
            <div className="lg:col-span-4 lg:sticky lg:top-24" data-aos="fade-right">
              <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 mb-8 overflow-hidden group text-center">
                <img
                  src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800'}
                  alt={product.name}
                  className="max-h-[750px] w-full object-contain mx-auto transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* 2x2 Status Grid */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                <div className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm">
                  <i className="fas fa-check-circle text-green-600 mr-2 text-xs"></i>
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-700">In Stock</span>
                </div>
                <div className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm">
                  <i className="fas fa-truck text-blue-600 mr-2 text-xs"></i>
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-700">Fast Delivery</span>
                </div>
                <div className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm">
                  <i className="fas fa-headset text-purple-600 mr-2 text-xs"></i>
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-700">24/7 Support</span>
                </div>
                <div className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm">
                  <i className="fas fa-shield-alt text-indigo-600 mr-2 text-xs"></i>
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-700">Warranty</span>
                </div>
              </div>

              {/* Main CTA: Enquire Now below image */}
              <button
                onClick={() => setEnquireModalOpen(true)}
                className="w-full bg-gradient-to-r from-[#c42121] to-[#a01a1a] text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-[0_15px_30px_rgba(196,33,33,0.4)] active:scale-95 flex items-center justify-center group"
              >
                <i className="fas fa-envelope mr-3 group-hover:rotate-12 transition-transform"></i>
                Enquire Now
              </button>
            </div>

            {/* Right Column: Title, Description, Features, Specs */}
            <div className="lg:col-span-8 space-y-10" data-aos="fade-left">
              <div>
                <div className="inline-flex items-center bg-slate-100 text-slate-600 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest mb-4">
                  {product.category?.name || 'Industrial'}
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                  {product.name}
                </h1>

                <p className="text-lg text-slate-600 leading-relaxed font-light">
                  {product.fullDescription || product.shortDescription || 'High-performance industrial solution designed for precision and reliability.'}
                </p>
              </div>

              {/* Core Capabilities */}
              {product.keyFeatures && product.keyFeatures.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center">
                    <span className="w-1.5 h-6 bg-db-blue mr-3"></span>
                    Core Capabilities
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {product.keyFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center p-3 rounded bg-slate-50 border border-slate-100 group hover:border-blue-200 transition-colors">
                        <i className="fas fa-check text-green-500 mr-3 text-xs"></i>
                        <span className="text-slate-700 font-semibold text-xs tracking-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Technical Parameters Table */}
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center">
                  <span className="w-1.5 h-6 bg-db-blue mr-3"></span>
                  Technical Parameters
                </h3>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-6 py-4 text-[9px] font-bold text-slate-500 uppercase tracking-widest">Technical Attribute</th>
                        <th className="px-6 py-4 text-[9px] font-bold text-slate-500 uppercase tracking-widest text-right">Configuration Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        { label: 'Asset Category', value: product.category?.name },
                        { label: 'Industry Subgroup', value: product.category?.parentCategory ? product.category?.name : 'Industrial Automation' },
                        { label: 'Integration Ports', value: product.ports },
                        { label: 'Operational Resolution', value: product.printWidth ? `${product.printWidth} DPI` : null },
                        { label: 'Certified Warranty', value: product.warranty },
                        ...(product.specifications ? Object.entries(product.specifications).map(([k, v]) => ({ label: k, value: v })) : [])
                      ].filter(item => item.value).map((spec, idx) => (
                        <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/30 transition-colors`}>
                          <td className="px-6 py-4 text-xs font-bold text-slate-500 w-1/3 leading-relaxed tracking-tight">{spec.label}</td>
                          <td className="px-6 py-4 text-xs font-bold text-slate-800 text-right font-mono tracking-tight">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="bg-slate-50 py-3 px-6 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Last Updated: Feb 2026</span>
                    <div className="flex items-center space-x-2">
                      <i className="fas fa-check-shield text-green-500 text-[10px]"></i>
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest italic">Verified Hardware</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '90%' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="flex flex-col items-center">
              <i className="fas fa-certificate text-3xl text-blue-600 mb-2"></i>
              <span className="text-xs font-bold text-gray-600 uppercase">Certified Partner</span>
            </div>
            <div className="flex flex-col items-center">
              <i className="fas fa-tools text-3xl text-blue-600 mb-2"></i>
              <span className="text-xs font-bold text-gray-600 uppercase">Expert Support</span>
            </div>
            <div className="flex flex-col items-center">
              <i className="fas fa-shield-alt text-3xl text-blue-600 mb-2"></i>
              <span className="text-xs font-bold text-gray-600 uppercase">Quality Insured</span>
            </div>
            <div className="flex flex-col items-center">
              <i className="fas fa-globe text-3xl text-blue-600 mb-2"></i>
              <span className="text-xs font-bold text-gray-600 uppercase">Global Standards</span>
            </div>
          </div>
        </div>
      </section>

      {/* Enquire Modal */}
      <EnquireModal
        isOpen={enquireModalOpen}
        onClose={() => setEnquireModalOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductDetails;
